import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Chip, Collapse, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Protocol } from '../../../types';

interface ProtocolRowProps {
  protocol: Protocol;
}

const ProtocolRow = ({ protocol }: ProtocolRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      medical: 'Médico',
      surgical: 'Quirúrgico',
      cardiac: 'Cardíaco',
      emergency: 'Emergencia',
      pediatric: 'Pediatría',
      geriatric: 'Geriatría',
    };
    return labels[category] || category;
  };

  // Ensure we have a valid ID
  if (!protocol._id) {
    return null;
  }

  // Ensure ID is a string
  const protocolId = String(protocol._id);

  const handleRowClick = (e: React.MouseEvent) => {
    // Only navigate if not clicking on the expand button
    const target = e.target as HTMLElement;
    if (
      !target.closest('button') &&
      !target.closest('[role="button"]') &&
      !target.closest('.MuiIconButton-root')
    ) {
      navigate(`/protocols/${protocolId}`);
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded(!expanded);
  };

  return (
    <>
      <TableRow
        hover
        onClick={handleRowClick}
        sx={{
          cursor: 'pointer',
        }}
      >
        <TableCell
          sx={{
            width: '500px',
            minWidth: '500px',
            maxWidth: '500px',
            overflow: 'hidden',
            paddingLeft: 1,
            paddingRight: 1,
          }}
        >
          <Typography
            variant='body2'
            fontWeight={500}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {protocol.title}
          </Typography>
        </TableCell>
        <TableCell
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          <Chip
            label={getCategoryLabel(protocol.category)}
            size='small'
            color='primary'
            variant='outlined'
          />
        </TableCell>
        <TableCell sx={{ width: '60px', textAlign: 'center' }} onClick={handleExpandClick}>
          <IconButton size='small' onClick={handleExpandClick} sx={{ pointerEvents: 'auto' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, borderBottom: 'none' }}>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'background.default', m: 1, borderRadius: 1 }}>
              <Typography variant='body2' color='text.secondary' paragraph>
                {protocol.description}
              </Typography>
              {protocol.tags && protocol.tags.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {protocol.tags.map(tag => (
                    <Chip key={tag} label={tag} size='small' variant='outlined' />
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ProtocolRow;
